import { Archive, CheckCircle2, FlagOff, X } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

interface BulkActionBarProps {
  count: number;
  onActivate: () => void;
  /** active→finish (BE transition matrix has no active→draft). */
  onFinish: () => void;
  onArchive: () => void;
  onClear: () => void;
  disabled?: boolean;
}

const BTN =
  "inline-flex h-8 items-center gap-1.5 rounded-[9px] px-3.5 text-[13px] font-semibold text-white transition disabled:opacity-50";

export function BulkActionBar({
  count,
  onActivate,
  onFinish,
  onArchive,
  onClear,
  disabled,
}: BulkActionBarProps): JSX.Element | null {
  const { t } = useTranslation("promotion");
  if (count === 0) return null;

  return (
    <div
      data-testid="bulk-action-bar"
      className="fixed bottom-6 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3.5 rounded-2xl px-4 py-2.5 pl-5 text-white shadow-[var(--sh-pop)]"
      style={{ background: "var(--surface-inverse, #131a29)" }}
    >
      <span className="whitespace-nowrap text-[13.5px] font-bold" data-testid="bulk-count">
        {t("bulk.selected", { count })}
      </span>
      <span className="h-5 w-px bg-white/15" aria-hidden />
      <button
        type="button"
        data-testid="bulk-activate"
        disabled={disabled}
        onClick={onActivate}
        className={`${BTN} bg-white/10 hover:bg-white/20`}
      >
        <CheckCircle2 className="h-4 w-4" aria-hidden />
        {t("bulk.activate")}
      </button>
      <button
        type="button"
        data-testid="bulk-finish"
        disabled={disabled}
        onClick={onFinish}
        className={`${BTN} bg-white/10 hover:bg-white/20`}
      >
        <FlagOff className="h-4 w-4" aria-hidden />
        {t("bulk.finish")}
      </button>
      <button
        type="button"
        data-testid="bulk-archive"
        disabled={disabled}
        onClick={onArchive}
        className={`${BTN} bg-[var(--intent-danger-fg)]/80 hover:bg-[var(--intent-danger-fg)]`}
      >
        <Archive className="h-4 w-4" aria-hidden />
        {t("bulk.archive")}
      </button>
      <button
        type="button"
        data-testid="bulk-clear"
        onClick={onClear}
        className="inline-flex h-8 items-center gap-1.5 rounded-[9px] px-2.5 text-[13px] text-white/50 transition hover:text-white"
      >
        <X className="h-4 w-4" aria-hidden />
        {t("bulk.clear")}
      </button>
    </div>
  );
}
